import { SkyMessageBar } from "@skypointcloud/skypoint-ui";
import { useQuery } from "@tanstack/react-query";
import type { RunEventRow } from "@shared/types/runs";
import { runEventsPath } from "@/hooks/use-runs";
import { leftOutSentence, parseLeftOut } from "@/lib/runs/leftOut";

/**
 * #1673: on a schedule's run, the connections it left out and why, from its own `RUN_CREATED`
 * event (ids and closed reasons only), so anyone who opens the run later reads it beside the run,
 * not only on the last row of a newest first events log. Nothing for a run that left nothing out,
 * for a run of one connection, or when the read fails: it is a note, never a blocker.
 */
export function RunLeftOutNote({
  runId,
  scheduleId,
  connectionNames,
}: {
  runId: string;
  scheduleId: string | null;
  connectionNames: ReadonlyMap<string, string>;
}) {
  const created = useQuery<RunEventRow[]>({
    queryKey: [`${runEventsPath(runId)}?event=RUN_CREATED&limit=1`],
    enabled: scheduleId !== null,
    retry: false,
    staleTime: Infinity,
  });
  const leftOut = parseLeftOut(
    (created.data?.[0]?.payload as { leftOut?: unknown } | null | undefined)?.leftOut,
  );
  const sentence = leftOutSentence(leftOut, (id) => connectionNames.get(id));
  if (!sentence) return null;
  return (
    <SkyMessageBar
      intent="info"
      layout="multiline"
      title={
        leftOut.length === 1
          ? "One connection of the schedule did not run"
          : "Some connections of the schedule did not run"
      }
      message={sentence}
      testId="run-left-out-note"
    />
  );
}
